import {
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Divider,
	ExternalLinkIndicator,
	Image,
	Text,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from '@volkswagen-onehub/icons-core';
import Skeleton from 'react-loading-skeleton';
import { VehicleObject } from '../../models/Vehicle';
import { FeatureServicesContext } from '../../use-feature-services';
import DataField from '../DataField/DataField';
import './EngineDetails.scss';
import 'react-loading-skeleton/dist/skeleton.css';
import { DateTimeFormat } from '../../utils/DateTimeFormat';
import { DataRequestState } from '../../models/DataRequestState';

export default function EngineDetails(props: Props) {
	const {
		hasViewPrivateDataRight,
		toggleVehicleVerificationModal,
		vehicle,
		isShareLink,
		technicalDataLoaded,
		trackingService,
		isLoggedIn,
		vehicleDataIncomplete,
		getAppConnectUrl,
		vinCheckTrackingAppConnect,
	} = props;
	const { t } = useTranslation('ns1', { useSuspense: false });

	const featureServices = useContext(FeatureServicesContext);
	let baseUrl = '';
	if (featureServices && featureServices['fa-content-service']) {
		baseUrl = featureServices['fa-content-service']!.content.baseAssetUrl;
	}

	const vinCheckTrackingButton = () => {
		if (trackingService) {
			trackingService.track('VWBasic_Button_Click', {
				eventInfo: {
					eventAction: 'VWBasic_Button_Click',
					pageId: window.location.href,
					pageName: 'Vin Details',
					contentId: 'vinDetails.engineDetails.unlockButton',
					sectionName: 'engineDetails',
					brand: 'passenger',
				},
				user: {
					loginStatus: isLoggedIn,
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				personalization: {
					personalizable: false,
				},
				configuration: {
					CarlineName: vehicle.data.modelCarLine,
					SalesgroupName: vehicle.data.modelSalesGroup,
				},
				link: {
					name: 'toggleVehicleVerificationModal',
				},
			});
		}
	};

	const getText = (dummy: 'short' | 'medium' | 'long', text: string | undefined) => {
		return vehicle.state !== DataRequestState.Available && !hasViewPrivateDataRight ? t(`dummyText.${dummy}`) : text;
	};

	const deliveryCountry = () => {
		if (!hasViewPrivateDataRight) {
			return (
				<div className="height-thirty-two">
					<Lock aria-label={t('general.locked')} />
				</div>
			);
		}

		if (!vehicle.data.vehicleDetails?.deliveryCountryName) {
			return <Skeleton height={32} />;
		}

		return (
			<div className="parent">
				{/* TODO 03122021 Define what to show if no deliveryCountryNameEnglish is available */}
				<Image
					className="flag-size"
					src={`${baseUrl}/assets/vinDetails/flags_svg/_Flags_svg_${vehicle.data.vehicleDetails?.deliveryCountryNameEnglish}.svg`}
					alt="flag"
				/>
				<Text appearance={TokenTextAppearance.bigcopy100}>{vehicle.data.vehicleDetails?.deliveryCountryName}</Text>
			</div>
		);
	};

	const computeCountryField = () => {
		return <DataField title={t('engineDetails.fromCountry')} jsxContent={deliveryCountry()} property="" />;
	};

	// TODO 14122021 The date format should be country specific and set in the backend
	const registrationDate = () => {
		if (!hasViewPrivateDataRight) {
			return (
				<div className="height-thirty-two">
					<Lock aria-label={t('general.locked')} />
				</div>
			);
		}

		if (!vehicle.data.vehicleDetails?.registrationDate) {
			return <Skeleton height={32} />;
		}

		return (
			<Text appearance={TokenTextAppearance.bigcopy100}>{DateTimeFormat.germanDateFormat(vehicle.data.vehicleDetails.registrationDate).toString()}</Text>
		);
	};

	const computeRegistrationDateField = () => {
		return (
			<DataField
				title={t('engineDetails.registrationDate')}
				jsxContent={
					vehicle.state !== DataRequestState.Available && !hasViewPrivateDataRight ? (
						<div translate="no">
							<Text>{t('dummyText.short')}</Text>
						</div>
					) : (
						registrationDate()
					)
				}
				property=""
			/>
		);
	};

	return (
		<>
			<Container wrap="always" gutter={ContainerGutter.dynamic0050} padding={{ top: ContainerPadding.dynamic0050 }}>
				<>
					<DataField title={t('engineDetails.motor')} property={getText('medium', vehicle.data.vehicleDetails?.engine)} jsxContent={undefined} />
					<Divider />
				</>

				<>
					<DataField
						title={t('engineDetails.enginePower')}
						property={getText('short', vehicle.data.vehicleDetails?.enginePower)}
						jsxContent={undefined}
					/>
					<Divider />
				</>

				<>
					<DataField
						title={t('engineDetails.transmission')}
						property={getText('medium', vehicle.data.vehicleDetails?.transmission)}
						jsxContent={undefined}
					/>
					<Divider />
				</>

				<>
					{computeRegistrationDateField()}
					<Divider />
				</>

				<>
					{computeCountryField()}
					<Divider />
				</>

				<>
					<DataField title={t('appConnect.title')} property={t(`appConnect.text.${vehicle.data.appConnectStatus}`)} jsxContent={undefined} />
					<Divider />
				</>

				<div className="app-connect-container">
					<Container verticalAlign="center" stretchContent wrap="never">
						<Image className="app-connect-icon-large" src={`${baseUrl}/assets/vinDetails/VW-App-Connect-Icon-D6_M.svg`} alt="app-connect-icon" />

						<CTA
							tag="a"
							href={getAppConnectUrl()}
							target="_blank"
							emphasis="secondary"
							stretchContent
							onClick={() => vinCheckTrackingAppConnect('page')}
						>
							{t(vehicle.data.appConnectStatus === 'locked' ? 'appConnect.activate' : 'appConnect.moreInformation')} <ExternalLinkIndicator />
						</CTA>
					</Container>
				</div>
			</Container>

			{!isShareLink && !hasViewPrivateDataRight && (
				<Container padding={{ top: ContainerPadding.dynamic0130 }} stretchContent>
					<CTA
						disabled={vehicleDataIncomplete || technicalDataLoaded !== DataRequestState.Available}
						tag="button"
						onClick={() => {
							vinCheckTrackingButton();
							toggleVehicleVerificationModal();
						}}
						emphasis="primary"
						stretchContent
					>
						{t('engineDetails.unlockButton')}
					</CTA>
				</Container>
			)}
		</>
	);
}

interface Props {
	hasViewPrivateDataRight: boolean;
	toggleVehicleVerificationModal: () => void;
	vehicle: VehicleObject;
	isShareLink: boolean;
	technicalDataLoaded: DataRequestState;
	trackingService: any;
	isLoggedIn: boolean;
	vehicleDataIncomplete: boolean;
	getAppConnectUrl: () => string;
	vinCheckTrackingAppConnect: (trackingLocation: 'banner' | 'page') => void;
}
